import { useState, useRef, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { ArrowLeft, Star, CheckCircle2 } from "lucide-react"
import { ReviewCard } from "./ReviewCard"
import { useAuthState } from "@/hooks/useAuth"
import { useUserData } from "@/hooks/useUserData"
import { submitReview, checkUserHasBookingWithArtist } from "@/lib/reviewData"

interface ReviewWriteContentProps {
  artistId: string
  artistName?: string
  onBack?: () => void
}

export function ReviewWriteContent({ artistId, artistName, onBack }: ReviewWriteContentProps) {
  const [rating, setRating] = useState(0)
  const [hoveredRating, setHoveredRating] = useState(0)
  const [reviewText, setReviewText] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isAnonymous, setIsAnonymous] = useState(false)
  const [photos, setPhotos] = useState<string[]>([])
  const [uploadingImagesCount, setUploadingImagesCount] = useState(0)
  const [isVerifiedClient, setIsVerifiedClient] = useState<boolean | null>(null) // null = loading
  const [photosForVerificationOnly, setPhotosForVerificationOnly] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  
  const { user } = useAuthState()
  const { userData } = useUserData()

  // Check if user has a booking with this artist
  useEffect(() => {
    const checkVerification = async () => {
      if (!user?.uid || !artistId) {
        setIsVerifiedClient(false)
        return
      }

      const hasBooking = await checkUserHasBookingWithArtist(user.uid, artistId)
      setIsVerifiedClient(hasBooking)
    }

    checkVerification()
  }, [user?.uid, artistId])

  const handleImageButtonClick = () => {
    fileInputRef.current?.click()
  }

  const handleImageSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!user) {
      alert("You must be logged in to upload photos")
      return
    }

    try {
      const files = Array.from(event.target.files || []).filter(f => f.type.startsWith('image/'))
      
      if (files.length === 0) {
        return
      }
      
      // Validate file sizes (max 10MB per file)
      const maxFileSize = 10 * 1024 * 1024
      const validFiles = files.filter(f => {
        if (f.size > maxFileSize) {
          alert(`File ${f.name} is too large. Maximum 10MB per image.`)
          return false
        }
        return true
      })
      
      if (validFiles.length === 0) {
        alert('File size exceeds 10MB limit. Please select smaller images.')
        return
      }
      
      const currentCount = photos.length
      const availableSlots = Math.max(0, 3 - currentCount)
      if (availableSlots <= 0) {
        alert('Maximum 3 photos allowed')
        return
      }
      
      const filesToUpload = validFiles.slice(0, availableSlots)
      
      // Upload each file immediately
      setUploadingImagesCount(prev => prev + filesToUpload.length)
      
      for (const file of filesToUpload) {
        try {
          const { uploadImageToStorage } = await import('@/lib/firebase')
          const permanentUrl = await uploadImageToStorage(file, 'review-images', user.uid)
          
          // Add the permanent URL to selected images
          setPhotos(prev => [...prev, permanentUrl])
        } catch (error) {
          console.error('Upload failed for', file.name, error)
          alert('Failed to upload image. Please try again.')
        } finally {
          setUploadingImagesCount(prev => prev - 1)
        }
      }
    } catch (error) {
      console.error('Error selecting images:', error)
      alert('Error selecting images. Please try again.')
      setUploadingImagesCount(0)
    }

    // Reset the input value to allow selecting the same file again
    if (event.target) {
      event.target.value = ''
    }
  }

  const handleRemoveImage = (index: number) => {
    setPhotos(prev => prev.filter((_, i) => i !== index))
  }

  const handleSubmit = async () => {
    if (rating === 0) {
      alert("Please select a rating")
      return
    }
    
    if (!reviewText.trim()) {
      alert("Please write a review")
      return
    }

    if (!user?.uid) {
      alert("You must be logged in to submit a review")
      return
    }

    // If user is not verified, they must upload at least one photo
    if (!isVerifiedClient && photos.length === 0) {
      alert("Please upload at least one photo to verify your booking")
      return
    }

    setIsSubmitting(true)
    
    try {
      // Determine where photos go based on verification status and user choice
      const reviewUrls = (isVerifiedClient || !photosForVerificationOnly) ? photos : []
      const verificationUrls = (!isVerifiedClient && photosForVerificationOnly) ? photos : []

      await submitReview(
        artistId,
        user.uid,
        rating,
        reviewText,
        {
          username: userData?.instagram || user.displayName || "User",
          userAvatarUrl: userData?.avatarUrl || user?.photoURL || undefined,
          isAnonymous: isAnonymous,
          reviewUrls: reviewUrls,
          verificationUrls: verificationUrls,
          isVerifiedClient: isVerifiedClient ?? false,
          isApproved: isVerifiedClient ?? false // Auto-approve verified clients
        }
      )
      
      // Show success message instead of calling onSuccess
      setSubmitted(true)
    } catch (error) {
      console.error("Error submitting review:", error)
      alert("Failed to submit review. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleBack = () => {
    if (onBack) {
      onBack()
    }
  }

  // Show success message after submission
  if (submitted) {
    return (
      <div className="bg-background md:pt-[52px] pb-24 min-h-screen">
        <div className="phone-container-padded pb-4 pt-safe-native-top-cushion md:pt-4 flex flex-col">
          {/* Header with back button */}
          <div className="flex items-center mb-6">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBack}
              className="h-10 w-10 rounded-full"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-xl font-semibold ml-4">
              {artistName ? (
                <>
                  <span className="text-muted-foreground">Review for </span>
                  <span className="text-foreground">{artistName}</span>
                </>
              ) : "Write a Review"}
            </h1>
          </div>

          {/* Success Message */}
          <div className="flex flex-col items-center justify-center mt-12 px-4">
            <CheckCircle2 className="w-16 h-16 text-green-500 mb-4" />
            <h2 className="text-2xl font-semibold text-foreground mb-3 text-center">
              {isVerifiedClient ? "Thank you for your review!" : "Review submitted for verification"}
            </h2>
            <p className="text-muted-foreground text-center max-w-md">
              {isVerifiedClient 
                ? "Your review has been published and will help other clients find great artists."
                : "We'll verify your review and make it visible as soon as possible. Thank you!"}
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-background md:pt-[52px] pb-24">
      <div className="phone-container-padded pb-4 pt-safe-native-top-cushion md:pt-4 flex flex-col">
        {/* Header with back button */}
        <div className="flex items-center mb-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleBack}
            className="h-10 w-10 rounded-full"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-semibold ml-4">
            {artistName ? (
              <>
                <span className="text-muted-foreground">Review for </span>
                <span className="text-foreground">{artistName}</span>
              </>
            ) : "Write a Review"}
          </h1>
        </div>


        {/* Preview Card - Only visible when showPreview is true */}
        {showPreview ? (
          <div className="mb-3 pt-2 pb-3">
            <p className="text-base font-medium text-foreground mb-2">
              Preview
            </p>
            <ReviewCard
              rating={rating}
              text={reviewText}
              username={userData?.instagram || "Anonymous"}
              avatarUrl={userData?.avatarUrl || user?.photoURL || undefined}
              date={new Date()}
              isAnonymous={isAnonymous}
              isPreview={true}
              reviewUrls={photosForVerificationOnly ? [] : photos}
            />
          </div>
        ) : (
          <>
            {/* Anonymous Toggle */}
            <div className="mb-3 flex items-center justify-between py-3 gap-6">
          <div className="flex-1 min-w-0 pr-2">
            <p className="text-base font-medium text-foreground">Post anonymously</p>
            <p className="text-sm text-muted-foreground">Your instagram handle and photo will be hidden</p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={isAnonymous}
            onClick={() => setIsAnonymous(!isAnonymous)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 flex-shrink-0 ${
              isAnonymous ? 'bg-primary' : 'bg-input'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                isAnonymous ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {/* Form Content */}
        {/* Rating Section */}
        <div className="mb-4">
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setRating(star)}
                onMouseEnter={() => setHoveredRating(star)}
                onMouseLeave={() => setHoveredRating(0)}
                className="transition-transform hover:scale-110"
              >
                <Star
                  className={`w-10 h-10 ${
                    star <= (hoveredRating || rating)
                      ? "fill-[#FFA500] text-[#FFA500]"
                      : "text-muted-foreground"
                  }`}
                />
              </button>
            ))}
          </div>
          {rating > 0 && (
            <p className="text-sm text-muted-foreground mt-1">
              {rating === 1 && "Poor"}
              {rating === 2 && "Fair"}
              {rating === 3 && "Good"}
              {rating === 4 && "Very Good"}
              {rating === 5 && "Excellent"}
            </p>
          )}
        </div>

        {/* Review Text */}
        <div className="mb-4">
          <div className="relative">
            <textarea
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              placeholder="Share your experience..."
              className="w-full min-h-[160px] p-4 pb-8 border border-muted-foreground rounded-lg focus:outline-none resize-none bg-background text-foreground placeholder-muted-foreground"
              maxLength={500}
            />
            <p className="absolute bottom-2 right-3 text-xs text-muted-foreground pb-4">
              {reviewText.length}/500
            </p>
          </div>
          
          {/* Add Photos Section */}
          <div className="mt-4">
            <div className="grid grid-cols-3 gap-3">
              {/* Display uploaded photos */}
              {photos.map((photo, index) => (
                <div
                  key={index}
                  className="relative w-full aspect-square rounded-lg overflow-hidden border border-muted-foreground"
                >
                  <img
                    src={photo}
                    alt={`Review photo ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveImage(index)}
                    className="absolute top-1 right-1 bg-background text-muted-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm hover:text-foreground transition-colors border border-muted-foreground"
                  >
                    ×
                  </button>
                </div>
              ))}
              
              {/* Upload Loading State or Add Photo Button */}
              {uploadingImagesCount > 0 ? (
                <div className="w-full aspect-square rounded-lg border border-muted-foreground flex flex-col items-center justify-center">
                  <div className="w-6 h-6 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : photos.length < 3 ? (
                <button
                  type="button"
                  onClick={handleImageButtonClick}
                  className="w-full aspect-square rounded-lg border border-muted-foreground hover:border-primary transition-colors flex flex-col items-center justify-center text-muted-foreground hover:text-primary"
                >
                  <span className="text-3xl mb-1">+</span>
                  <span className="text-sm">Add Photo</span>
                </button>
              ) : null}
            </div>

            {/* Photo Visibility Toggle - Only show for non-verified users with photos */}
            {!isVerifiedClient && photos.length > 0 && (
              <div className="mb-3 flex items-center justify-between py-3 gap-6">
                <div className="flex-1 min-w-0 pr-2">
                  <p className="text-base font-medium text-foreground">Display in review</p>
                  <p className="text-sm text-muted-foreground">
                    {photosForVerificationOnly 
                      ? "Photos will not be visible on your review"
                      : "Photos will be visible on your review once approved"}
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={!photosForVerificationOnly}
                  onClick={() => setPhotosForVerificationOnly(!photosForVerificationOnly)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 flex-shrink-0 ${
                    !photosForVerificationOnly ? 'bg-primary' : 'bg-input'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      !photosForVerificationOnly ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            )}

            {/* Verification Note - Only show for non-verified users without photos, appears after photos */}
            {!isVerifiedClient && photos.length === 0 && (
              <div className="mt-4">
                <p className="text-sm text-foreground">
                  <span className="font-bold">Note:</span> To help us verify your experience, please upload at least one photo from your appointment.
                </p>
              </div>
            )}
          </div>

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleImageSelect}
            className="hidden"
          />
        </div>
          </>
        )}
      </div>

      {/* Action Buttons - Fixed at bottom */}
      <div className="fixed bottom-0 left-0 right-0 bg-background p-4" style={{ maxWidth: 'calc(var(--max-content-width) + (var(--min-side-padding) * 2))', marginLeft: 'auto', marginRight: 'auto' }}>
        <div className="phone-container-padded flex gap-3">
          <Button
            onClick={() => setShowPreview(!showPreview)}
            variant="secondary"
            className="flex-1 h-14 text-base font-medium rounded-[0.6rem] flex items-center justify-center gap-2"
          >
            {showPreview ? "Edit" : "Preview"}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || rating === 0 || !reviewText.trim() || (!isVerifiedClient && photos.length === 0) || isVerifiedClient === null}
            className="flex-1 h-14 text-base font-medium rounded-[0.6rem] flex items-center justify-center gap-2"
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </Button>
        </div>
      </div>
    </div>
  )
}
